// PURPOSE: Own one visible row cursor and Outreach keyboard dispatch.
// CONTRACT: Spec 078 B1; editable fields and dialogs retain native keys.
// DEPENDS: React and explicit data-row-id markers. CEILING: No record mutations.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useRowCursor(ids: string[], command?: (key: string, id?: string) => void) {
 const ref = useRef<HTMLDivElement>(null);
 const [selected, setSelected] = useState<string>();
 const previous = useRef(ids);
 const cursor = selected && !ids.includes(selected)
  ? (previous.current.slice(previous.current.indexOf(selected) + 1).find(id => ids.includes(id)) ?? ids[Math.min(previous.current.indexOf(selected), ids.length - 1)]) : selected;
 useLayoutEffect(() => { previous.current = ids; if (selected !== cursor) setSelected(cursor); }, [ids, selected, cursor]);
 useLayoutEffect(() => {
  const rows = ref.current?.querySelectorAll<HTMLElement>('[data-row-id]');
  rows?.forEach(row => row.classList.toggle('is-cursor', row.dataset.rowId === cursor));
  const row = Array.from(rows || []).find(row => row.dataset.rowId === cursor);
  row?.scrollIntoView?.({ block: 'nearest' });
 }, [cursor]);
 useEffect(() => {
  const onKey = (event: KeyboardEvent) => {
   const target = event.target instanceof Element ? event.target : document.activeElement;
   if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || (event.shiftKey && event.key !== '?') || target?.closest('input,textarea,select,[contenteditable]:not([contenteditable="false"])') || document.querySelector('dialog[open], [role="dialog"]:not([hidden]), [role="alertdialog"]:not([hidden])')) return;
   // Focused controls keep Enter and arrows; row shortcuts never hijack an open menu.
   if (target?.closest('[role="menu"]') || (['Enter', 'ArrowDown', 'ArrowUp'].includes(event.key) && target?.closest('button,a,summary'))) return;
   const rows = Array.from(ref.current?.querySelectorAll<HTMLElement>('[data-row-id]') || []).filter(row => !row.closest('details:not([open]),[hidden]'));
   const visible = rows.map(row => row.dataset.rowId!).filter(id => ids.includes(id));
   if (['j', 'k', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
    if (!visible.length) return;
    event.preventDefault(); const index = cursor ? visible.indexOf(cursor) : -1;
    setSelected(visible[index < 0 ? 0 : Math.max(0, Math.min(visible.length - 1, index + (['j', 'ArrowDown'].includes(event.key) ? 1 : -1)))]);
   } else if (event.key === 'Escape') { setSelected(undefined); }
   else if (event.key === '?' || (cursor && visible.includes(cursor) && ['Enter', 'c', 'p', 's', 'x'].includes(event.key))) {
    if (event.repeat) return;
    event.preventDefault(); command?.(event.key, cursor);
   }
  };
  document.addEventListener('keydown', onKey);
  return () => document.removeEventListener('keydown', onKey);
 }, [ids, cursor, command]);
 return { ref, cursor };
}
